import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Navigation, Camera, Loader2, Share2, FileDown, StickyNote, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg, uploadFile } from "@/lib/api";
import { eur, fmtLongDate, KIND, mapsLink } from "@/lib/format";
import { shareReport, buildReportPdf, downloadBlob, reportFileName } from "@/lib/pdf";
import { StatusBadge, SectionLabel } from "@/components/Brand";
import { MediaGrid } from "@/components/MediaThumb";
import { CloseJobForm } from "@/pages/tech/CloseJobForm";
import { ChatThread } from "@/components/Chat";
import { pingNow } from "@/lib/tracking";
import { SeriesDays, DayDoneForm } from "@/pages/tech/SeriesDays";
import { CantiereCloseForm } from "@/pages/tech/CantiereCloseForm";
import { useAuth } from "@/lib/auth";
import { AwayBox } from "@/components/AwayBox";
import { Input } from "@/components/ui/input";
import { StartJobButton } from "@/components/StartJobButton";
import { ReportWaButton } from "@/components/ReportWaButton";

const fmtTs = (s) => new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function PlanBox({ job, isLead, onRead }) {
  const [busy, setBusy] = useState(false);
  const read = async () => {
    setBusy(true);
    try { onRead((await api.post(`/jobs/${job.id}/plan-read`)).data); toast.success("Lettura confermata all'ufficio"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#cfe3b5] bg-[#f6faf0] p-3" data-testid="tech-job-work-plan">
      <p className="text-xs font-semibold uppercase tracking-wide text-[#3d6b16]">Programma di lavoro</p>
      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{job.work_plan}</p>
      {job.work_plan_read_at ? <p className="mt-2 text-xs text-[#3d6b16]" data-testid="tech-plan-read-status">Letto il {fmtTs(job.work_plan_read_at)}</p>
        : isLead && <Button size="sm" className="mt-2 rounded-full bg-[#61a229] hover:bg-[#528a22]" onClick={read} disabled={busy} data-testid="tech-plan-read-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confermo di aver letto</Button>}
    </div>
  );
}

function AckOtherDate({ job, onAccept }) {
  const [open, setOpen] = useState(false);
  const [w, setW] = useState({ date: job.date, start: job.start, end: job.end });
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="text-sm font-semibold text-[#3d6b16] hover:underline" data-testid="tech-ack-other-date">Accetto ma con un'altra data o orario</button>;
  const upd = (k) => (e) => setW((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="space-y-2 rounded-xl border bg-white p-3" data-testid="tech-ack-other-box">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2 space-y-1 text-xs text-slate-600">Data<Input type="date" value={w.date} onChange={upd("date")} data-testid="tech-ack-date" /></label>
        <label className="space-y-1 text-xs text-slate-600">Dalle<Input type="time" value={w.start} onChange={upd("start")} data-testid="tech-ack-start" /></label>
        <label className="space-y-1 text-xs text-slate-600">Alle<Input type="time" value={w.end} onChange={upd("end")} data-testid="tech-ack-end" /></label>
      </div>
      <Button className="w-full rounded-full bg-[#61a229] hover:bg-[#528a22]" onClick={() => onAccept(w)} data-testid="tech-ack-accept-date">Accetto con questa data</Button>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <section className="rounded-2xl border bg-white p-4 space-y-3"><SectionLabel>{title}</SectionLabel>{children}</section>
);

function MediaUploader({ job, onAdded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) onAdded(await uploadFile(`/jobs/${job.id}/media`, f));
      toast.success("File caricati");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="outline" className="w-full rounded-full" onClick={() => ref.current?.click()} disabled={busy} data-testid="media-upload-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Scatta / carica foto o video
      </Button>
      <input ref={ref} type="file" accept="image/*,video/*" multiple hidden onChange={pick} data-testid="media-upload-input" />
    </>
  );
}

function ReportActions({ job }) {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const r = await shareReport(job);
      if (r === "downloaded") toast.info("Condivisione non supportata: PDF scaricato");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non riuscita");
    } finally {
      setBusy(false);
    }
  };
  const download = async () => downloadBlob(await buildReportPdf(job), reportFileName(job));
  return (
    <div className="grid grid-cols-2 gap-2">
      <Button onClick={share} disabled={busy} className="rounded-full h-12" data-testid="report-share-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Condividi PDF
      </Button>
      <Button variant="outline" onClick={download} className="rounded-full h-12" data-testid="report-download-button"><FileDown className="h-4 w-4" /> Scarica</Button>
      <div className="col-span-2"><ReportWaButton jobId={job.id} testid="tech-report-wa-button" className="h-12 w-full" /></div>
    </div>
  );
}

export default function TechJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  const nav = useNavigate();
  const { user } = useAuth();
  if (!job) return <Loader2 className="h-5 w-5 animate-spin" />;

  const start = async () => {
    try { setJob((await api.post(`/jobs/${id}/start`)).data); toast.success("Intervento avviato"); pingNow("job_start", id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const ack = async (accept, when = null) => {
    const note = accept ? "" : window.prompt("Motivo del rifiuto (facoltativo)") ?? null;
    if (note === null) return;
    try {
      const { data } = await api.post(`/tech/jobs/${id}/ack`, { accept, note, ...(when || {}) });
      if (accept) { setJob(data); toast.success(when ? "Intervento accettato con la nuova data: il cliente riceve la conferma" : "Intervento accettato: il cliente riceve la conferma"); } else { toast.success("Intervento rifiutato"); nav("/tecnico"); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const helper = job.technician_id !== user?.id;
  const lead = !helper;
  const open = ["confermato", "in_corso"].includes(job.status) && job.tech_ack !== "in_attesa";
  const midDay = job.series_id && job.series_index < job.series_total;

  return (
    <div className="space-y-4 pb-6">
      <Link to="/tecnico" className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="tech-job-back"><ArrowLeft className="h-4 w-4" /> Agenda</Link>
      <div className="rise">
        <SectionLabel>{KIND[job.kind]}</SectionLabel>
        <h1 className="mt-1 font-display text-2xl font-bold">{job.client.first_name} {job.client.last_name}</h1>
        <p className="text-sm text-slate-500">{fmtLongDate(job.date)}{job.end_date ? ` → ${fmtLongDate(job.end_date)}` : ""} · {job.start}–{job.end}</p>
        <div className="mt-2"><StatusBadge status={job.status} testid="tech-job-status" /></div>
      </div>
      {helper && <p className="rounded-2xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900" data-testid="tech-helper-note">Sei <b>aiutante</b> su {job.kind === "cantiere" ? "questo cantiere" : "questo intervento"}: il rapporto lo compila il responsabile, <b>{job.technician_name}</b>. Puoi caricare foto e scrivere in chat.</p>}
      {lead && job.helpers?.length > 0 && <p className="text-sm text-slate-600" data-testid="tech-helpers-list">Aiutanti: {job.helpers.map((h) => h.name).join(", ")}</p>}
      {lead && job.tech_ack === "in_attesa" && (
        <div className="space-y-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="tech-ack-box">
          <p className="text-sm font-semibold text-amber-900">{job.series_id ? (job.site?.recurring ? "Nuovo cantiere con orario ripetitivo assegnato: confermi la disponibilità?" : `Nuovo cantiere di ${job.series_total} giorni assegnato: confermi la disponibilità per tutti i giorni?`) : "Nuovo intervento assegnato: confermi la disponibilità?"}</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => ack(false)} data-testid="tech-ack-reject">Rifiuto</Button>
            <Button className="rounded-full bg-[#61a229] hover:bg-[#528a22]" onClick={() => ack(true)} data-testid="tech-ack-accept">Accetto</Button>
          </div>
          {!job.series_id && <AckOtherDate job={job} onAccept={(when) => ack(true, when)} />}
          <p className="text-xs text-amber-800">Il cliente riceve la conferma su WhatsApp solo dopo che accetti.</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" className="rounded-full" data-testid="tech-call-button"><a href={`tel:${job.client.phone}`}><Phone className="h-4 w-4" /> Chiama</a></Button>
        <Button asChild variant="outline" className="rounded-full" data-testid="tech-navigate-button"><a href={mapsLink(job)} target="_blank" rel="noreferrer"><Navigation className="h-4 w-4" /> Naviga</a></Button>
      </div>
      <Panel title="Dettagli">
        {job.series_id && <AwayBox away={job.away} />}
        {job.series_id && <SeriesDays job={job} />}
        <p className="text-sm">{job.client.address}, {job.client.city}</p>
        {job.client.phone && <a href={`tel:${job.client.phone}`} className="flex gap-2 text-sm font-medium text-[#3d6b16]" data-testid="tech-job-phone"><Phone className="h-4 w-4 text-[#61a229]" />{job.client.phone}</a>}
        {(job.equipment_label || job.brand_model) && <p className="flex gap-2 text-sm"><Wrench className="h-4 w-4 text-[#61a229]" />{[job.equipment_label, job.brand_model].filter(Boolean).join(" · ")}</p>}
        {job.warranty && <p className="w-fit rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700" data-testid="tech-job-warranty">Intervento in garanzia</p>}
        {job.notes && <p className="flex gap-2 text-sm text-slate-600"><StickyNote className="h-4 w-4 shrink-0 text-[#61a229]" />{job.notes}</p>}
        {job.work_plan && <PlanBox job={job} isLead={job.technician_id === user?.id} onRead={setJob} />}
        {job.error_photos?.length > 0 && <MediaGrid items={job.error_photos} prefix="tech-error-photos" />}
      </Panel>
      {lead && job.status === "confermato" && job.tech_ack !== "in_attesa" && (
        <StartJobButton job={job} onStart={start} />
      )}
      <Panel title="Foto e video dal campo">
        <MediaGrid items={job.media} prefix="tech-media" />
        {open && <MediaUploader job={job} onAdded={(m) => setJob((j) => ({ ...j, media: [...j.media, m] }))} />}
      </Panel>
      <Panel title="Chat con l'ufficio su questo intervento">
        <ChatThread jobId={job.id} className="h-80" />
      </Panel>
      {lead && open && midDay ? (
        <Panel title="Giornata di cantiere"><DayDoneForm job={job} onDone={setJob} /></Panel>
      ) : lead && open ? (
        <Panel title={job.series_id ? "Chiusura cantiere" : "Chiusura lavoro"}>{job.site ? <CantiereCloseForm job={job} onClosed={setJob} /> : <CloseJobForm job={job} onClosed={setJob} />}</Panel>
      ) : job.day_log ? (
        <Panel title="Giornata svolta"><p className="whitespace-pre-line text-sm" data-testid="tech-day-log">{job.day_log.notes || `Giornata segnata come svolta da ${job.day_log.by}`}</p></Panel>
      ) : job.status === "sospeso" ? (
        <Panel title="Intervento sospeso">
          {(() => { const s = job.suspensions?.[job.suspensions.length - 1] || {}; return (<div className="space-y-1 text-sm" data-testid="tech-suspended-box">
            <p className="font-semibold text-amber-700">Motivo: {s.reason}</p>
            <p className="whitespace-pre-line">{s.summary}</p>
            <div className="flex justify-between border-t pt-2 font-semibold"><span>Totale parziale</span><span className="font-mono">{eur(s.total)}</span></div>
            <p className="text-xs text-slate-500">Pagamento {s.paid ? "effettuato" : "in sospeso"} · l'ufficio riprogrammerà il completamento</p>
          </div>); })()}
          <ReportActions job={job} />
        </Panel>
      ) : job.closure && (
        <Panel title="Rapporto di intervento">
          <p className="whitespace-pre-line text-sm" data-testid="tech-closure-summary">{job.closure.summary}</p>
          <div className="space-y-1 text-sm">
            {job.closure.no_prices ? (<>
              {(job.closure.team_hours || []).map((t) => <div key={t.id} className="flex justify-between"><span>{t.name}</span><span className="font-mono">{t.hours} h</span></div>)}
              <div className="flex justify-between border-t pt-2 font-semibold"><span>Totale ore squadra</span><span className="font-mono" data-testid="tech-closure-hours">{job.closure.hours} h</span></div>
              {job.closure.parts?.map((p, i) => <div key={i} className="flex justify-between text-slate-600"><span>{p.name}</span><span className="font-mono">x {p.qty}</span></div>)}
            </>) : (<>
            <div className="flex justify-between"><span>Diritto di chiamata</span><span className="font-mono">{eur(job.closure.call_fee)}</span></div>
            <div className="flex justify-between"><span>Manodopera ({job.closure.billed_minutes} min)</span><span className="font-mono">{eur(job.closure.time_cost)}</span></div>
            {job.closure.parts.map((p, i) => <div key={i} className="flex justify-between text-slate-600"><span>{p.qty} × {p.name}</span><span className="font-mono">{eur(p.total)}</span></div>)}
            {job.closure.discount > 0 && <div className="flex justify-between border-t pt-2 text-amber-700" data-testid="tech-closure-discount"><span>Sconto ({job.closure.discount_reason})</span><span className="font-mono">-{eur(job.closure.discount)}</span></div>}
            <div className="flex justify-between border-t pt-2 font-semibold"><span>Totale</span><span className="font-mono" data-testid="tech-closure-total">{eur(job.closure.total)}</span></div>
            </>)}
          </div>
          {job.closure.signature ? (
            <div data-testid="tech-closure-signature"><p className="text-xs text-slate-500">Firma: {job.closure.signer_name}</p>
              <img src={job.closure.signature} alt="Firma cliente" className="mt-1 h-20 rounded-lg border bg-white" /></div>
          ) : <p className="text-xs text-slate-500" data-testid="tech-closure-no-signature">Firma non acquisita</p>}
          <ReportActions job={job} />
        </Panel>
      )}
    </div>
  );
}
